<template>
  <collapse :active-index="0">
    <collapse-item body-classes="px-0 pb-1">
      <template v-if="collapse" slot="title">
        <h3 class="mb-0">{{ title }}</h3>
      </template>
      <template v-else slot="header">
        <h3 class="mb-0">{{ title }}</h3>
      </template>

      <div>
        <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
          <el-select v-model="pagination.perPage" class="select-primary pagination-select mt-1" placeholder="Per page">
            <el-option
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
              class="select-primary"
            >
            </el-option>
          </el-select>

          <base-input v-if="!filterCampaign && campaigns.length > 0" class="mt-1">
            <el-select v-model="campaignSelected" class="select-primary" placeholder="Campaña" value-key="value">
              <el-option :value="null" class="select-primary" label="Todas las campañas"></el-option>
              <el-option
                v-for="item in campaigns"
                :key="item.name"
                :label="item.name"
                :value="item.id"
                class="select-primary"
              >
              </el-option>
            </el-select>
          </base-input>

          <base-input v-if="showStateFilter && states.length > 0" class="mt-1">
            <el-select v-model="mixStateFilter" class="select-primary" placeholder="Estado" value-key="value">
              <el-option :value="null" class="select-primary" label="Todos"> </el-option>
              <el-option
                v-for="item in states"
                :key="item.label"
                :label="item.label + 's'"
                :value="item.id"
                class="select-primary"
              >
              </el-option>
            </el-select>
          </base-input>

          <div>
            <base-input v-model="mixSearchQuery" class="mt-1" placeholder="Buscar..." prepend-icon="fas fa-search">
            </base-input>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <el-table :data="queriedData" header-row-class-name="thead-light" @sort-change="sortChange">
          <el-table-column type="expand">
            <template #default="{ row }">
              <ul class="list-group list-group-flush list my--3">
                <li v-for="item in row.items" :key="item.id" class="list-group-item px-0">
                  <div class="align-items-center">
                    <div class="row">
                      <div class="col-md-1 col-sm-12">{{ item.article.code }}</div>
                      <div class="col-md-3 col-sm-12">
                        <span class="font-weight-bold">{{ item.quantity }}</span
                        >&nbsp;x&nbsp;
                        <span>{{ item.article.description }}</span>
                      </div>
                      <div class="col-md-2 col-sm-12">{{ item.price | toCurrencyUSD }} .- c/u</div>
                      <div class="col-md-2 col-sm-12">{{ (item.price * item.quantity) | toCurrencyUSD }}</div>
                    </div>
                  </div>
                </li>
                <li v-if="row.items.length" class="list-group-item px-0">
                  <div class="row">
                    <div class="col-md-1 offset-md-5 col-sm-1 offset-sm-1">
                      <span class="font-weight-bold">Total</span>
                    </div>
                    <div class="col-md-2 col-sm-10">
                      <span>{{ row.items.reduce((acc, i) => (acc += i.quantity * i.price), 0) | toCurrencyUSD }}</span>
                    </div>
                  </div>
                </li>
                <li v-if="!row.items.length" class="list-group-item px-0">Sin artículos.</li>
              </ul>
            </template>
          </el-table-column>

          <el-table-column label="Código" min-width="140px" prop="code" sortable>
            <template #default="{ row }">
              {{ row.code }}
            </template>
          </el-table-column>
          <el-table-column label="Fecha" min-width="120px" prop="date" sortable>
            <template #default="{ row }">
              {{ row.date | date('DD/MM/YYYY') }}
            </template>
          </el-table-column>

          <el-table-column label="Productor" min-width="150px" prop="producer" sortable>
            <template #default="{ row }">
              <div v-if="row.producer">
                <router-link
                  v-if="$can('show', 'producers')"
                  :to="{ name: 'ProducerShow', params: { id: row.producer.id } }"
                  class="h5"
                >
                  {{ row.producer.businessName }}
                </router-link>
                <p v-else>{{ row.producer.businessName }}</p>
              </div>
              <div v-if="row.producer.distributor">
                <small>{{ row.producer.distributor.name }}</small>
              </div>
              <div v-if="row.producer.distributor.seller">
                <small>{{ row.producer.distributor.seller.name }}</small>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Autor" min-width="120px" prop="user" sortable>
            <template #default="{ row }">
              {{ row.user.name }}
            </template>
          </el-table-column>

          <el-table-column label="Campaña" min-width="140px" prop="campaign" sortable>
            <template #default="{ row }">
              <div>{{ row.campaign.name }}</div>
            </template>
          </el-table-column>

          <el-table-column label="Estado" min-width="120px" prop="state" sortable>
            <template #default="{ row }">
              <div v-if="row.deletedAt">
                <badge type="danger" class="m-1">Eliminado</badge>
                <small class="text-break">por {{ row.updatedBy.name }} el {{ row.deletedAt | date }}</small>
              </div>
              <badge v-else :type="getColor(row)" class="m-1">{{ row.state.label }}</badge>
            </template>
          </el-table-column>

          <el-table-column align="right" label="" min-width="180px">
            <div slot-scope="{ row }" class="d-flex">
              <div v-if="!row.deletedAt">
                <router-link
                  v-if="$can('show', 'quotations')"
                  :to="{ name: 'QuotationShow', params: { id: row.id } }"
                  class="btn base-button btn-outline-info btn-sm"
                  title="Ver"
                >
                  <i class="fas fa-eye"></i>
                </router-link>
                <router-link
                  v-if="$can('update', 'quotations') && !isSendedQuotation(row)"
                  :to="{ name: 'QuotationEdit', params: { id: row.id } }"
                  class="btn base-button btn-outline-primary btn-sm"
                  title="Editar"
                >
                  <i class="fas fa-pen"></i>
                </router-link>
              </div>
            </div>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <div class="">
          <p class="card-category">Mostrando {{ from + 1 }} a {{ to }} de un total de {{ pagination.total }} pedidos</p>
        </div>
        <base-pagination
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :show-first-page-button="true"
          :show-last-page-button="true"
          :total="pagination.total"
          class="pagination-no-border"
          @input="changePage"
        >
        </base-pagination>
      </div>
    </collapse-item>
  </collapse>
</template>

<script>
import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown, Select, Option } from 'element-ui';
import { mapState } from 'vuex';

import { BasePagination } from '@/components';
import Collapse from '@/components/Collapse/Collapse';
import CollapseItem from '@/components/Collapse/CollapseItem';

import QuotationsService from '@/services/resources/quotations.service';
import QuotationStatesMixin from '@/mixins/quotationStatesMixin';
import PaginationOnServerMixin from '@/mixins/paginationOnServerMixin';

export default {
  name: 'QuotationsTable',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    BasePagination,
    Collapse,
    CollapseItem,
  },

  mixins: [PaginationOnServerMixin, QuotationStatesMixin],

  props: {
    title: {
      type: String,
      default: 'Todas las cotizaciones',
    },

    collapse: {
      type: Boolean,
      default: false,
    },

    filterAuthor: {
      type: Number,
      default: null,
    },

    filterDistributor: {
      type: Number,
      default: null,
    },

    filterProducer: {
      type: Number,
      default: null,
    },

    filterCampaign: {
      type: Number,
      default: null,
    },

    showStateFilter: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      tableData: [],
      campaignSelected: null,
      modelName: 'quotation',
    };
  },

  computed: {
    ...mapState('models/campaign', ['campaigns']),
  },

  watch: {
    filterCampaign: {
      immediate: true,
      handler() {
        this.fetchTableData();
      },
    },
  },

  methods: {
    fetchTableData() {
      this.pagination.perPage = this.perPage || 50;
      this.pagination.currentPage = this.page || this.pagination.currentPage;

      const params = {
        include: ['producer.distributor.seller', 'campaign', 'user', 'state', 'items.article'],
        'filter[user]': this.filterAuthor,
        'filter[distributor]': this.filterDistributor,
        'filter[producer]': this.filterProducer,
        'filter[campaign]': this.campaignSelected || this.filterCampaign,
        'filter[search]': this.mixSearchQuery,
        'filter[state]': this.mixStateFilter,
        perPage: this.pagination.perPage,
        page: this.pagination.currentPage,
        sort: this.sort || this.defaultSort,
      };

      this.$services
        .resource('quotations')
        .get(params)
        .then((res) => {
          this.setMeta(res.data.meta);
          return res.data.data;
        })
        .then((data) => (this.tableData = data.quotations));
    },

    isSendedQuotation(quotation) {
      return quotation.state !== null && quotation.state.name === QuotationsService._STATE_SENDED;
    },
  },
};
</script>

<style scoped>
::v-deep .el-table__expand-column .cell {
  width: 20px;
}

::v-deep .el-table__expanded-cell p {
  word-break: break-word;
  white-space: normal;
}
</style>
